import React, { useState, useEffect, createRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import FluidImg from 'Components/Utility/FluidImg';
import SpecialFormattedNumber from 'Components/Utility/Price/SpecialFormattedNumber';
import { currencyCode } from 'Helpers/Geo/data';

import 'Components/Cards/ScreenshotCarousel.scss';

/* eslint-disable react/style-prop-object */
function ScreenshotCarousel({
  screenshots: screenshotsProp,
  fullPrice,
  displayType,
  type,
  gameTotal,
  cardSize,
  isHovering,
  showCustomSlide,
}) {
  const [slideNumber, setSlideNumber] = useState(1);
  const carousel = createRef();

  useEffect(() => {
    if (isHovering) {
      carousel.current.style.opacity = 1;
    }
  }, [isHovering, carousel]);

  const isBundle = displayType === 'comic-bundle' || displayType === 'book-bundle' || displayType === 'elearning-bundle' || displayType === 'software-bundle' || displayType === 'audio-bundle' || type === 'bundle';
  const tallScreenshots = displayType === 'comic-bundle' || displayType === 'book-bundle' || displayType === 'elearning-bundle' || displayType === 'software-bundle' || displayType === 'audio-bundle';
  const isPromotion = (type === 'pick-and-mix');

  let screenshots = screenshotsProp.slice(0, 6);
  let screenshotLength = screenshots.length || 0;

  if (isBundle || isPromotion) {
    screenshots = screenshotsProp.slice(0, 5);
    screenshotLength = ((screenshots.length || 0) + 1);
  }

  const onMouseChange = (e) => {
    const rect = carousel.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const segment = width / screenshotLength;

    let slideToSet = 1;

    const screenshotLengthArray = [...Array(screenshotLength).keys()].reverse();

    screenshotLengthArray.forEach((num) => {
      const currentSlideNumber = num + 1;
      const segmentMin = segment * 1;
      const segmentMax = segment * currentSlideNumber;

      if (x > segmentMin && x < segmentMax) {
        slideToSet = currentSlideNumber;
      }
    });
    setSlideNumber(slideToSet);
  };

  const renderCustomSlide = () =>
    <div className={`screenshot-fallback ${slideNumber === screenshotLength ? '' : 'd-none'}`}>
      <div className={`screenshot-fallback-content screenshot-fallback-content-${cardSize}`}>
        <FormattedMessage
          id="screenshot.carousel.viewall"
          defaultMessage="View all {gameTotal} products"
          values={{
            gameTotal,
            price: <SpecialFormattedNumber
              value={fullPrice[currencyCode]}
              style="currency"
            />,
          }}
        />
      </div>
    </div>;

  return (
    <div
      className={classNames('hover-dot-screenshot-carousel', { 'tall-screenshots': tallScreenshots })}
      ref={carousel}
      onMouseMove={e => onMouseChange(e)}
    >
      <div className="screenshot-container">
        {tallScreenshots &&
          <React.Fragment>
            {screenshotLength >= 1 && screenshots.map((cover, index) =>
              <div className="screenshot-image" key={cover + 1}>
                <FluidImg src={cover} alt={cover} className={`screenshot-bg ${slideNumber === (index + 1) ? '' : 'd-none'}`} />
                <FluidImg src={cover} alt={cover} className={`${slideNumber === (index + 1) ? '' : 'd-none'}`} />
              </div>,
            )}
            {(isBundle || isPromotion) && showCustomSlide && renderCustomSlide()}
          </React.Fragment>
        }
        {!tallScreenshots &&
          <div className="responsive-image-island">
            <div className="responsive-image responsive-image--16by9">
              {screenshotLength >= 1 &&
                <React.Fragment>
                  {screenshots.map((cover, index) =>
                    <FluidImg ratio16by9 src={cover} alt={cover} className={`img-force-full ${slideNumber === (index + 1) ? '' : 'd-none'}`} key={cover + 1} />,
                  )}
                </React.Fragment>
              }
              {(isBundle || isPromotion) && showCustomSlide && renderCustomSlide()}
            </div>
          </div>
        }
      </div>
      {screenshotLength >= 1 &&
        <div className="screenshot-overlay" />
      }
      {screenshotLength > 1 &&
        <div className={`screenshot-progress-dot-container screenshot-progress-dot-container-${cardSize}`}>
          {[...Array((screenshotLength)).keys()].map((num, index) =>
            <div className={`screenshot-progress-dot ${slideNumber === (index + 1) ? 'active-dot' : ''}`} key={num + 1} />,
          )}
        </div>
      }
    </div>
  );
}

ScreenshotCarousel.propTypes = {
  screenshots: PropTypes.arrayOf(PropTypes.string),
  fullPrice: PropTypes.shape({}),
  displayType: PropTypes.string,
  type: PropTypes.string,
  gameTotal: PropTypes.number,
  cardSize: PropTypes.string,
  isHovering: PropTypes.bool,
  showCustomSlide: PropTypes.bool,
};

ScreenshotCarousel.defaultProps = {
  screenshots: [],
  fullPrice: {},
  displayType: 'game',
  type: 'game',
  gameTotal: 0,
  cardSize: 'small',
  isHovering: false,
  showCustomSlide: false,
};


export default ScreenshotCarousel;
